/**
 * Measure tool — point-to-point distance and bearing on the map.
 *
 * Activate via the map control button. Click two points to measure.
 * Shows distance (km/nm), bearing, and draws a geodesic line.
 * Click again or press Escape to clear and start a new measurement.
 */

import { getMap } from '@/map/engine';
import { haversineDistance, bearing, bearingToCompass } from '@/utils/geo';
import type maplibregl from 'maplibre-gl';

const KM_TO_NM = 0.539957;

interface MeasurePoint {
  lng: number;
  lat: number;
  marker: HTMLElement;
}

let active = false;
let points: MeasurePoint[] = [];
let lineSourceAdded = false;
let resultLabel: HTMLElement | null = null;
let mapClickHandler: ((e: maplibregl.MapMouseEvent) => void) | null = null;
let escHandler: ((e: KeyboardEvent) => void) | null = null;

/** Format distance with both km and nautical miles. */
function formatDistance(km: number): string {
  const nm = km * KM_TO_NM;
  if (km < 1) {
    return `${(km * 1000).toFixed(0)} m  (${nm.toFixed(2)} nm)`;
  }
  return `${km.toFixed(1)} km  (${nm.toFixed(1)} nm)`;
}

/** Create a small dot marker on the map. */
function createMarker(lng: number, lat: number): HTMLElement {
  const map = getMap();
  if (!map) throw new Error('Map not available');

  const el = document.createElement('div');
  el.className = 'measure-point';

  // Position via map.project
  const pos = map.project([lng, lat]);
  el.style.cssText = `
    position: absolute;
    left: ${pos.x - 5}px;
    top: ${pos.y - 5}px;
    width: 10px;
    height: 10px;
    background: var(--accent, #06b6d4);
    border: 2px solid white;
    border-radius: 50%;
    pointer-events: none;
    z-index: 20;
    box-shadow: 0 0 6px rgba(6, 182, 212, 0.6);
  `;

  const canvas = map.getCanvas();
  canvas.parentElement?.appendChild(el);

  // Update position on map move
  const update = (): void => {
    const p = map.project([lng, lat]);
    el.style.left = `${p.x - 5}px`;
    el.style.top = `${p.y - 5}px`;
  };
  map.on('move', update);
  (el as any).__cleanup = () => {
    map.off('move', update);
    el.remove();
  };

  return el;
}

/** Draw or update the measurement line between two points. */
function updateLine(): void {
  const map = getMap();
  if (!map || points.length < 2) return;

  const coords = points.map(p => [p.lng, p.lat]);

  if (!lineSourceAdded) {
    map.addSource('measure-line', {
      type: 'geojson',
      data: {
        type: 'Feature',
        properties: {},
        geometry: { type: 'LineString', coordinates: coords },
      },
    });
    map.addLayer({
      id: 'measure-line-layer',
      type: 'line',
      source: 'measure-line',
      paint: {
        'line-color': '#06b6d4',
        'line-width': 2,
        'line-dasharray': [4, 3],
      },
    });
    lineSourceAdded = true;
  } else {
    (map.getSource('measure-line') as maplibregl.GeoJSONSource)?.setData({
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: coords },
    });
  }
}

/** Show the distance/bearing result label on the map. */
function showResult(): void {
  if (points.length < 2) return;
  const [a, b] = points;
  const dist = haversineDistance(a.lat, a.lng, b.lat, b.lng);
  const brg = bearing(a.lat, a.lng, b.lat, b.lng);
  const compass = bearingToCompass(brg);

  const map = getMap();
  if (!map) return;

  // Position label at midpoint
  const mid = map.project([
    (a.lng + b.lng) / 2,
    (a.lat + b.lat) / 2,
  ]);

  if (!resultLabel) {
    resultLabel = document.createElement('div');
    resultLabel.className = 'measure-result';
    map.getCanvas().parentElement?.appendChild(resultLabel);
  }

  resultLabel.textContent = `${formatDistance(dist)}  •  ${brg.toFixed(0)}° ${compass}`;
  resultLabel.style.left = `${mid.x}px`;
  resultLabel.style.top = `${mid.y - 20}px`;
  resultLabel.style.display = 'block';

  // Update position on map move
  const updatePos = (): void => {
    const p = map.project([(a.lng + b.lng) / 2, (a.lat + b.lat) / 2]);
    if (resultLabel) {
      resultLabel.style.left = `${p.x}px`;
      resultLabel.style.top = `${p.y - 20}px`;
    }
  };
  map.on('move', updatePos);
  (resultLabel as any).__cleanup = () => {
    map.off('move', updatePos);
  };
}

/** Clear all measurement markers, line, and labels. */
function clearMeasurement(): void {
  const map = getMap();

  // Remove markers
  for (const p of points) {
    if ((p.marker as any).__cleanup) (p.marker as any).__cleanup();
  }
  points = [];

  // Remove line
  if (map && lineSourceAdded) {
    if (map.getLayer('measure-line-layer')) map.removeLayer('measure-line-layer');
    if (map.getSource('measure-line')) map.removeSource('measure-line');
    lineSourceAdded = false;
  }

  // Remove label
  if (resultLabel) {
    if ((resultLabel as any).__cleanup) (resultLabel as any).__cleanup();
    resultLabel.remove();
    resultLabel = null;
  }
}

/** Deactivate the measure tool entirely. */
export function deactivateMeasure(): void {
  if (!active) return;
  active = false;
  clearMeasurement();

  const map = getMap();
  if (map && mapClickHandler) {
    map.off('click', mapClickHandler);
    mapClickHandler = null;
  }

  if (escHandler) {
    document.removeEventListener('keydown', escHandler);
    escHandler = null;
  }

  // Restore default cursor
  if (map) map.getCanvas().style.cursor = '';

  // Update button state
  const btn = document.querySelector('.measure-tool-btn');
  if (btn) btn.classList.remove('active');
}

/** Activate the measure tool. */
export function activateMeasure(): void {
  if (active) {
    deactivateMeasure();
    return;
  }
  active = true;

  const map = getMap();
  if (!map) return;

  map.getCanvas().style.cursor = 'crosshair';

  // Update button state
  const btn = document.querySelector('.measure-tool-btn');
  if (btn) btn.classList.add('active');

  mapClickHandler = (e: maplibregl.MapMouseEvent) => {
    if (points.length >= 2) {
      clearMeasurement();
    }

    const marker = createMarker(e.lngLat.lng, e.lngLat.lat);
    points.push({ lng: e.lngLat.lng, lat: e.lngLat.lat, marker });

    if (points.length === 2) {
      updateLine();
      showResult();
    }
  };

  map.on('click', mapClickHandler);

  escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') deactivateMeasure();
  };
  document.addEventListener('keydown', escHandler);
}

/** Check if the measure tool is currently active. */
export function isMeasureActive(): boolean {
  return active;
}
